import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  InkRecognitionModel,
  RecognitionResult,
  RecognizePrepareField
} from '@authority-labs/api-interfaces';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subscription } from 'rxjs';
import { concatMap, finalize } from 'rxjs/operators';
import { from } from 'rxjs';
import { InspectionsService } from '../inspections.service';

const BATCH_SIZE = 8;

export interface DraftRecognitionDialogData {
  inspectionId: number;
}

export interface DraftRecognitionDialogResult {
  accepted: RecognitionResult[];
}

type DialogPhase = 'preparing' | 'ready' | 'converting' | 'done';

interface DisplayField extends RecognitionResult {
  selected: boolean;
  editing: boolean;
}

@Component({
  selector: 'authority-labs-draft-recognition-dialog',
  templateUrl: './draft-recognition-dialog.component.html',
  styleUrls: ['./draft-recognition-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DraftRecognitionDialogComponent implements OnInit, OnDestroy {
  results: DisplayField[] = [];
  phase: DialogPhase = 'preparing';
  error: string | null = null;
  model: InkRecognitionModel = 'gemini-3-flash';

  progress = { completed: 0, total: 0 };
  totalTokens = 0;
  startTime = 0;

  private convertSub: Subscription | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DraftRecognitionDialogData,
    public dialogRef: MatDialogRef<
      DraftRecognitionDialogComponent,
      DraftRecognitionDialogResult
    >,
    private inspectionsService: InspectionsService,
    private message: NzMessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.prepare();
  }

  ngOnDestroy(): void {
    this.convertSub?.unsubscribe();
  }

  prepare(): void {
    this.phase = 'preparing';
    this.error = null;
    this.results = [];
    this.cdr.markForCheck();

    this.inspectionsService
      .recognizePrepare(this.data.inspectionId, { model: this.model })
      .subscribe({
        next: (response) => {
          this.results = response.fields.map((f: RecognizePrepareField) => {
            const isCached = !!f.cached;
            return {
              fieldId: f.fieldId,
              fieldTitle: f.fieldTitle,
              fieldSection: f.fieldSection,
              expectedType: f.expectedType,
              inkImageBase64: f.inkImageBase64,
              notePath: f.notePath,
              rawText: isCached ? f.cached.rawText : '',
              typedValue: isCached ? f.cached.typedValue : null,
              confidence: isCached ? f.cached.confidence : 'low',
              cached: isCached,
              selected: isCached && !!f.cached.rawText,
              editing: false
            } as DisplayField;
          });
          this.phase = 'ready';
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = 'שגיאה בטעינת השדות. נסה שוב.';
          this.phase = 'ready';
          this.message.error('שגיאה בטעינת שדות כתב יד');
          this.cdr.markForCheck();
        }
      });
  }

  /** AI recognition - kept but hidden from UI for now */
  convert(): void {
    const uncachedIds = this.results
      .filter((r) => !r.rawText)
      .map((r) => r.fieldId);

    if (uncachedIds.length === 0) {
      this.phase = 'done';
      this.cdr.markForCheck();
      return;
    }

    const batches: string[][] = [];
    for (let i = 0; i < uncachedIds.length; i += BATCH_SIZE) {
      batches.push(uncachedIds.slice(i, i + BATCH_SIZE));
    }

    this.phase = 'converting';
    this.progress = { completed: 0, total: uncachedIds.length };
    this.totalTokens = 0;
    this.startTime = Date.now();
    this.error = null;
    this.cdr.markForCheck();

    this.convertSub = from(batches)
      .pipe(
        concatMap((batchIds) =>
          this.inspectionsService.recognizeBatch(
            this.data.inspectionId,
            batchIds
          )
        ),
        finalize(() => {
          this.phase = 'done';
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (batchResponse) => {
          for (const result of batchResponse.results) {
            const idx = this.results.findIndex(
              (r) => r.fieldId === result.fieldId
            );
            if (idx >= 0) {
              this.results[idx] = {
                ...this.results[idx],
                rawText: result.rawText,
                typedValue: result.typedValue,
                confidence: result.confidence,
                cached: false,
                selected: !!result.rawText && result.rawText !== '???'
              };
            }
          }
          this.progress.completed += batchResponse.results.length;
          this.totalTokens += batchResponse.usage.totalTokens;
          this.cdr.markForCheck();
        },
        error: () => {
          this.message.error('שגיאה בזיהוי חלק מהשדות');
          this.results.forEach((r) => {
            if (!r.rawText) {
              r.rawText = '???';
              r.confidence = 'low';
              r.selected = false;
            }
          });
          this.phase = 'done';
          this.cdr.markForCheck();
        }
      });
  }

  /** Import draft and merge edited fields */
  applyDraft(): void {
    this.convertSub?.unsubscribe();
    const accepted = this.results
      .filter((r) => r.selected && r.rawText)
      .map(({ selected, editing, ...rest }) => rest);
    this.dialogRef.close({ accepted });
  }

  cancel(): void {
    this.convertSub?.unsubscribe();
    this.dialogRef.close(null);
  }

  get editedCount(): number {
    return this.results.filter((r) => r.selected && r.rawText).length;
  }

  get sections(): string[] {
    const sectionSet = new Set(this.results.map((r) => r.fieldSection));
    return Array.from(sectionSet);
  }

  getResultsBySection(section: string): DisplayField[] {
    return this.results.filter((r) => r.fieldSection === section);
  }

  startEdit(result: DisplayField): void {
    result.editing = true;
  }

  saveEdit(result: DisplayField, newValue: string): void {
    const trimmed = newValue.trim();
    result.rawText = trimmed;
    result.typedValue = trimmed || null;
    result.confidence = trimmed ? 'high' : 'low';
    result.editing = false;
    result.selected = !!trimmed;
  }

  cancelEdit(result: DisplayField): void {
    result.editing = false;
  }

  clearField(result: DisplayField): void {
    result.rawText = '';
    result.typedValue = null;
    result.confidence = 'low';
    result.cached = false;
    result.selected = false;
  }
}
